import { ImageName } from "@mcleod/images";
import {
    Button,
    ButtonVariant, ChangeEvent,
    ClickEvent,
    Label,
    Layout,
    Panel,
    SlideoutDecorator,
    Snackbar,
    Toast
} from "@mcleod/components";
import {Alignment, Api, HorizontalAlignment, Navigation, StringUtil} from "@mcleod/core";
import { AutogenLayoutOnboardCarrier } from "./autogen/AutogenLayoutOnboardCarrier";
import {CommonTooltips} from "@mcleod/common";

export class OnboardCarrier extends AutogenLayoutOnboardCarrier {
    private _movementId: string;
    private _prequalResult: any;
    private _allowIdentifyCarrier: boolean;
    private _carrierEmail: string;
    private _vendorName: string;

    constructor(props?) {
        super(props);
        this._movementId = props.movementId;
        this._prequalResult = props.prequalResult;
        this._allowIdentifyCarrier = props.allowIdentifyCarrier
        this._carrierEmail = props.carrierEmail;
        this._vendorName = props.vendorName;
    }

    override onLoad(): void | Promise<void> {
        if (this._allowIdentifyCarrier) {
            this.panelIdentifyCarrier.visible = true;
            this.panelOnboardGeneral.visible = false;
        } else if (!this._prequalResult) {
            this.tabManual.select();
            this.tabAuto.visible = false;
            this.textboxManualCarrierEmail.text = this._carrierEmail;
            this.addLayoutDataLoadListener(() =>
                setTimeout(() =>
                        Snackbar.showWarningSnackbar(this.getCannotOnboardMessage()),
                    500
                )
            )
        } else if (this._prequalResult?.highway_vendor) {
            this._prequalResult = (this._prequalResult as HighwayPrequalResult);
            this.configureForHighway();
        } else if (this._prequalResult?.aa_vendor) {
            this._prequalResult = (this._prequalResult as AAPrequalResult);
            this.configureForAssureAdvantage();
        } else if (this._prequalResult?.rmis_vendor) {
            this._prequalResult = (this._prequalResult as RMISPrequalResult);
            this.configureForRmis();
        }

        if (this._movementId) {
            this.mainDataSource.search({"movement_id": this._movementId, "prequal": this._prequalResult})
                .then(response => {

                    if (response.modelRows[0].data.pending_carrier_name) {
                        this.textboxManualCarrierName.text = response.modelRows[0].data.pending_carrier_name
                    }

                    if (response.modelRows[0].data.comments) {
                        this.textboxComments.text = response.modelRows[0].data.comments
                    }


                    this.textboxCarrierEmail.addBeforeLookupModelSearchListener(event => {
                        event.filter.prequal = this._prequalResult;
                    });
                    this.buttonSave.enabled = true;
                });
        }
    }

    private getCannotOnboardMessage() {
        const vendorName = (this._vendorName || "vendor") + "'s";
        return `Cannot automatically onboard, carrier does not exist in ${vendorName} system. You may still invite using email.`;
    }

    private configureForHighway() {
        this.panelOnboardGeneral.visible = true;
        this.panelOnboardRmis.visible = false;
        this.textboxPendingCarrierName.text = this._prequalResult.company_name;
        this.textboxPendingCarrierName.enabled = false;
        this.textboxMyCarrierPacketsUser.visible = false;
        this.labelPendingInvitationGeneral.visible = this._prequalResult.carrier_invited;
    }

    private configureForAssureAdvantage() {
        this.panelOnboardGeneral.visible = true;
        this.textboxMyCarrierPacketsUser.visible = true;
        this.panelOnboardRmis.visible = false;
        this.textboxPendingCarrierName.text = this._prequalResult.company_name;
        this.textboxPendingCarrierName.enabled = false;
        this.labelPendingInvitationGeneral.visible = this._prequalResult.carrier_invited;
    }

    private configureForRmis() {
        this.panelOnboardGeneral.visible = false;
        this.panelOnboardRmis.visible = true;
        this.rmisCarrierName.text = this._prequalResult.company_name;
        this.labelRmisPendingInvitation.visible = this._prequalResult.carrier_invited;
    }

    private buttonSave = new Button({
        rowBreak: false,
        backgroundColor: "primary",
        color: "primary.reverse",
        caption: "Invite Carrier",
        borderWidth: 0,
        borderColor: "primary",
        visible: true,
        enabled: false,
        onClick: (event: ClickEvent) => this.inviteCarrier()
    });

    static showSlideout(movementId: string, prequalResult: any, allowIdentifyCarrier: boolean, carrierEmail: string, vendorName?: string) {
        const layout = Layout.getLayout("lme/dispatch/OnboardCarrier",
            {
                movementId: movementId,
                prequalResult: prequalResult,
                allowIdentifyCarrier: allowIdentifyCarrier,
                carrierEmail: carrierEmail,
                vendorName: vendorName
            } as any
        ) as OnboardCarrier;

        const slideoutDecorator = new SlideoutDecorator({
            title: "Invite Carrier",
            layout: layout,
            fillVerticalSpace: true,
            width: 1000,
            overlayProps: {greyedBackground: true},
            addlComponents: layout.buttonSave,
            onClose: (cancelled: boolean) => layout.slideOut()
        });
    }

    imageDetailOnClick() {
        if (!StringUtil.isEmptyString(this.textboxManualCarrierEmail.text)
            && !StringUtil.isEmptyString(this.textboxSubject.text)
            && !StringUtil.isEmptyString(this.textboxComments.text)) {
            let mailToLink = `mailto:${encodeURI(this.textboxManualCarrierEmail.text)}?`;
            if (!StringUtil.isEmptyString(this.textboxCc.text)) {
                mailToLink = mailToLink + `cc=${this.textboxCc.text}&`;
            }
            if (!StringUtil.isEmptyString(this.textboxBcc.text)) {
                mailToLink = mailToLink + `bcc=${this.textboxBcc.text}&`;
            }
            mailToLink = mailToLink + `subject=${encodeURI(this.textboxSubject.text)}&body=${encodeURI(this.textboxComments.text)}`;
            window.open(mailToLink, '_blank');
            this.slideOut();
        } else {
            Snackbar.showWarningSnackbar("Please provide all fields to invite a carrier.");
        }
    }

    buttonIdentifyCarrierOnClick(event: ClickEvent) {
        this.buttonIdentifyCarrier.busy = true;
        const numberType: string = this.textboxNumberType.text;
        const carrierNumber: string = this.textboxCarrierNumber.text;
        if (StringUtil.isEmptyString(numberType)
            || StringUtil.isEmptyString(carrierNumber)
        ) {
            this.buttonIdentifyCarrier.busy = false;
            Snackbar.showWarningSnackbar("Number Type and Carrier Number required to identify carrier.");
        } else {
            Api.post("lme/powerbroker/prequal-carrier", {
                identifier_type: numberType,
                identifier_number: carrierNumber
            }).then(response => {
                this.buttonIdentifyCarrier.busy = false;
                const existInCarrierSystem = response?.data[0]?.exist_in_vendor_system;
                if (!existInCarrierSystem) {
                    this.panelOnboardGeneral.visible = false;
                    this.panelOnboardRmis.visible = false;
                  /*  this.buttonSave.visible = false;*/
                    Snackbar.showWarningSnackbar(this.getCannotOnboardMessage());
                } else {
                    const result: any = response.data[0]
                    if (result?.highway_vendor) {
                        this._prequalResult = (result as HighwayPrequalResult);
                        this.configureForHighway();
                    } else if (result?.aa_vendor) {
                        this._prequalResult = (result as AAPrequalResult);
                        this.configureForAssureAdvantage();
                    } else if (result?.rmis_vendor) {
                        this._prequalResult = (result as RMISPrequalResult);
                        this.configureForRmis();
                    }
                    this.textboxCarrierEmail.addBeforeLookupModelSearchListener(event => {
                        event.filter.prequal = this._prequalResult;
                    });
                    this.buttonSave.enabled = true;
                    this.setupInvitedCarrierTooltip();
                }
            })
        }
    }

    inviteCarrier() {
        if (this.tabAuto.selected) {
            if (this._prequalResult?.rmis_vendor) {
                if (!StringUtil.isEmptyString(this.rmisCarrierName.text)
                    && !StringUtil.isEmptyString(this.rmisContactName.text)
                    && !StringUtil.isEmptyString(this.rmisContactEmail.text)
                    && !StringUtil.isEmptyString(this.rmisUserName.text)
                    && !StringUtil.isEmptyString(this.rmisUserEmail.text)) {
                    this.onboardWithRmis();
                } else {
                    Snackbar.showWarningSnackbar("Please provide all fields to invite a carrier.");
                }
            } else if (this._prequalResult?.highway_vendor || this._prequalResult?.aa_vendor) {
                if (!StringUtil.isEmptyString(this.textboxCarrierEmail.text)) {
                    if (this._prequalResult?.highway_vendor) {
                        this.onboardWithHighway();
                    } else if (this._prequalResult?.aa_vendor) {
                        this.onboardWithAA();
                    }
                } else {
                    Snackbar.showWarningSnackbar("Please provide all fields to invite a carrier.");
                }
            }
        } else if (this.tabManual.selected) {
            if (!StringUtil.isEmptyString(this.textboxManualCarrierEmail.text)
                && !StringUtil.isEmptyString(this.textboxSubject.text)
                && !StringUtil.isEmptyString(this.textboxComments.text)) {
                this.onboardManually();
            } else {
                Snackbar.showWarningSnackbar("Please provide all fields to invite a carrier.");
            }
        }
    }

    onboardManually() {
        this.buttonSave.busy = true;
        Api.post("lme/dispatch/send-carrier-invite", {
            "to": this.textboxManualCarrierEmail.text,
            "cc": this.textboxCc.text,
            "bcc": this.textboxBcc.text,
            "subject": this.textboxSubject.text,
            "body": this.textboxComments.text,
            "prequal": this._prequalResult
        })
            .then(response => {
                this.buttonSave.busy = false;
                if (response?.data[0]?.success) {
                    if (response?.data[0]?.carrier_added) {
                        this.slideOut().then(() => {
                            const message = `Invitation created with ${this._prequalResult.vendor_name}.`;
                            if (response?.data[0]?.carrier_added && !StringUtil.isEmptyString(response?.data[0]?.payee_id)) {
                                const carrierAddedMessage = `Carrier, ${response?.data[0]?.payee_id} (${response?.data[0]?.payee_id}) has been added.`;
                                const toast = this.createSuccessToastPanel(message, carrierAddedMessage, response?.data[0]?.payee_id)
                                Toast.showToast(toast, {backgroundColor: "primary"}, {millisUntilDismissal: 10000});
                            } else if (!StringUtil.isEmptyString(message)) {
                                Toast.showSuccessToast('Success!', message);
                            }
                        });
                    } else {
                        this.slideOut().then(() => {
                            Toast.showSuccessToast('Success!', `Carrier Invitation sent`), {millisUntilDismissal: 10000};
                        });
                    }
                } else {
                    Snackbar.showWarningSnackbar("There was an unexpected error inviting the carrier.");
                }
            });
    }

    onboardWithRmis() {
        this.buttonSave.busy = true;
        Api.post("lme/dispatch/invite-carrier",
            {
                "pending_carrier_name": this.rmisCarrierName.text,
                "contact_name": this.rmisContactName.text,
                "contact_email": this.rmisContactEmail.text,
                "user_name": this.rmisUserName.text,
                "user_email": this.rmisUserEmail.text,
                "prequal": this._prequalResult,
                "movement_id": this._movementId
            })
            .then(response => {
                this.buttonSave.busy = false;
                this.handleInviteResponse(response);
            })

    }

    onboardWithHighway() {
        this.buttonSave.busy = true;
        Api.post("lme/dispatch/invite-carrier", {
            carrier_email: this.textboxCarrierEmail.text,
            prequal: this._prequalResult,
            "movement_id": this._movementId
        }).then(response => {
            this.buttonSave.busy = false;
            this.handleInviteResponse(response);
        }).catch(() => {
            this.buttonSave.busy = false;
            Snackbar.showWarningSnackbar("An error occurred while processing the request.");
        });
    }

    onboardWithAA() {
        this.buttonSave.busy = true;
        Api.post("lme/dispatch/invite-carrier", {
            "carrier_email": this.textboxCarrierEmail.text,
            "carrier_packets_user": this.textboxMyCarrierPacketsUser.text,
            "prequal": this._prequalResult,
            "movement_id": this._movementId
        })
            .then(response => {
                this.buttonSave.busy = false;
                this.handleInviteResponse(response);
            })
    }

    handleInviteResponse(response) {
        const inviteResponse = response?.data[0]?.invite_response;

        if (!inviteResponse) {
            Snackbar.showWarningSnackbar("There was an unexpected error processing the invitation.");
            return;
        }

        const {status_code, carrier_added, payee_id, connection_invitation, email, auto_add_on, message} = inviteResponse;
        const errorMessages = new Set();
        let hasStatusError = false;

        if (status_code === 422) {
            const requestError = connection_invitation?.[0] || `email: ${email}` || "There was an issue with your request format.";
            errorMessages.add(requestError);
            hasStatusError = true;
        } else if(status_code === 400){
           if (!StringUtil.isEmptyString(message) && message.includes("failed the Assure Advantage risk assessment")) {
                errorMessages.add("The invitation can not be sent to this carrier due to a failed risk assessment.");
            } else {
                errorMessages.add(message || "There was an issue with your request.");
            }
            hasStatusError = true;
        }else if (![200, 201].includes(status_code)) {
            errorMessages.add("There was an unexpected error inviting the carrier.");
            hasStatusError = true;
        }

        if (!carrier_added && auto_add_on) {
            errorMessages.add("There was an unexpected error adding the carrier.");
        }


        const invitationMessage = hasStatusError ? null : `Invitation created with ${this._prequalResult.vendor_name}.`;
        const carrierAddedMessage = carrier_added ? `Carrier, ${payee_id} (${payee_id}) has been added.` : null;

        // Display success toast
        this.slideOut().then(() => {
            if (!invitationMessage && !carrierAddedMessage) {
                if (errorMessages.size > 0) {
                    const errorMessage = Array.from(errorMessages).join(" ");
                    Snackbar.showWarningSnackbar(errorMessage, { millisUntilDismissal: 10000 });
                }
                return
            }

            if (carrierAddedMessage) {
                const toast = this.createSuccessToastPanel(invitationMessage, carrierAddedMessage, payee_id)
                Toast.showToast(toast, {backgroundColor: "primary"}, {millisUntilDismissal: 10000});
            } else if (!StringUtil.isEmptyString(invitationMessage)) {
                Toast.showSuccessToast('Success!', invitationMessage);
            }
        });
    }


    createSuccessToastPanel(inviteMessage: string, addedMessage: string, carrierId: string): Panel {
        const labelProps = {
            color: "white",
            fontSize: "xlarge",
            rowBreak: false,
            paddingLeft: 3,
            paddingRight: 1,
            marginLeft: 0,
            marginRight: 0,
            wrap: false
        };
        return new Panel({
            align: HorizontalAlignment.CENTER,
            fillRow: true,
            components: [
                new Label({...labelProps, text: "Success!", rowBreak: true}),
                new Label({...labelProps, text: inviteMessage, rowBreak: true}),
                new Label({...labelProps, text: addedMessage, rowBreak: false}),
                new Button({
                    imageName: ImageName.detail,
                    imageWidth: 20,
                    imageHeight: 20,
                    margin: 0,
                    tooltip: "Click to go to Carrier screen",
                    color: "subtle.light",
                    variant: ButtonVariant.round,
                    onClick: () => Navigation.navigateTo("lme/dispatch/Carrier?mode=update&key=" + carrierId, {newTab: true})
                })
            ]
        });
    }



    textboxNumberTypeOnChange(event: ChangeEvent) {
        this.buttonIdentifyCarrier.visible = !StringUtil.isEmptyString(event.newValue) && !StringUtil.isEmptyString(this.textboxCarrierNumber.text);
    }

    textboxCarrierNumberOnChange(event: ChangeEvent) {
        this.buttonIdentifyCarrier.visible = !StringUtil.isEmptyString(event.newValue) && !StringUtil.isEmptyString(this.textboxNumberType.text)
    }

    private setupInvitedCarrierTooltip() {
        const tooltipConfig = {
            minWidth: 100,
            minHeight: 100
        };

        const tooltipPosition = {position: Alignment.RIGHT};

        const carrierData = {
            dot_number: this._prequalResult?.dot_number,
            mc_number: this._prequalResult?.mc_number
        };

        const labels = [this.labelRmisPendingInvitation, this.labelPendingInvitationGeneral];

        labels.forEach(label => {
            CommonTooltips.setTooltipContentLayout(label, null, "lme/powerbroker/CarrierInvitedQuickInfo", tooltipConfig, carrierData);
            label.tooltipDynamicOptions = tooltipPosition;
        });
    }
}

class HighwayPrequalResult {
    classifications: string;
    company_name: string;
    dot_number: number;
    exist_in_vendor_system: boolean;
    highway_vendor: boolean;
    mc_number: string;
    overall_result: string;
    qualified_carrier: boolean
    vendor_name: string;
    vendor_url: string;
    highway_id: string;
}

class AAPrequalResult {
    aa_authority: string;
    aa_insurance: string;
    aa_operation: string;
    aa_other: string;
    aa_safety: string;
    aa_vendor: boolean;
    company_name: string;
    dot_number: number;
    exist_in_vendor_system: boolean;
    mc_number: string;
    qualified_carrier: boolean;
    risk_assessment: string;
    vendor_name: string;
    vendor_url: string;
}

class RMISPrequalResult {
    vendor_name: string;
    rmis_vendor: boolean;
    exist_in_vendor_system: boolean;
    company_name: string;
    mc_number: string;
    dot_number: string;
    insurance_ok: string;
    dot_ok: string;
    qualified_carrier: boolean;
    invitation_request: object;
}
